import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { serviceType } from '../constants/ServiceType';
import { orderStatus } from '../constants/OrderStatus';
import { modalExtras } from '../constants/ModalExtras';
import { TranslateService } from '@ngx-translate/core';
import { gearboxType } from '../constants/GearboxType';
import { driveType } from '../constants/DriveType';
import { ecoStickerType } from '../constants/EcoSticker';
import { vehicleStatus } from '../constants/VehicleStatus';
import { itvStatus } from '../constants/ITVStatus';
import { currency } from '../constants/Currency';
import { paymentMethod } from '../constants/PaymentMethod';
import { insuranceType } from '../constants/InsuranceType';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';

type BackendCatalogItem = {
  key: number;
  value: string;
};

type SelectOption = {
  value: number;
  labelKey: string;
  text: string;
};

@Injectable({
  providedIn: 'root',
})
export class GetValuesService {
  private readonly chassisTypeIconMap: Record<number, string> = {
    1: 'sedan',
    2: 'cuv',
    3: 'suv',
    4: 'hatchback',
    5: 'roadster',
    6: 'pickup',
    7: 'atv',
    8: 'coupe',
    9: 'supercar',
    10: 'campervan',
    11: 'minitruck',
    12: 'cabriolet',
    13: 'minivan',
    14: 'truck',
    15: 'lightvan',
    16: 'van',
    17: 'boxtruck',
    18: 'micro',
    19: 'scooter',
    20: 'motorbike',
    21: 'other'
  };
  private readonly apiBaseUrl = environment.cafler_backoffice_back_url;
  private readonly fuelTypesPath = '/Vehicles/fuel-types';
  private readonly vehicleTypesPath = '/Vehicles/types';
  private readonly tireRadiusPath = '/tire-products/radius';
  private readonly fuelTypeOptionsSubject = new BehaviorSubject<SelectOption[]>([]);
  private readonly vehicleTypeOptionsSubject = new BehaviorSubject<SelectOption[]>([]);
  private readonly tireRadiusOptionsSubject = new BehaviorSubject<number[]>([]);
  private fuelTypeCatalogItems: BackendCatalogItem[] = [];
  private vehicleTypeCatalogItems: BackendCatalogItem[] = [];

  readonly fuelTypeOptions$ = this.fuelTypeOptionsSubject.asObservable();
  readonly vehicleTypeOptions$ = this.vehicleTypeOptionsSubject.asObservable();
  readonly tireRadiusOptions$ = this.tireRadiusOptionsSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.translate.onLangChange.subscribe(() => {
      void this.refreshFuelTypeOptions();
      void this.refreshVehicleTypeOptions();
    });
    this.loadDynamicCatalogs();
  }

  private async loadDynamicCatalogs(): Promise<void> {
    await Promise.allSettled([
      this.loadFuelTypesFromBackend(),
      this.loadVehicleTypesFromBackend(),
      this.loadTireRadiusFromBackend()
    ]);
  }

  private async getAuthHeaders(): Promise<HttpHeaders | null> {
    const token = await this.authService.getAccessToken();
    if (!token) {
      return null;
    }
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private async loadFuelTypesFromBackend(): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      if (!headers) return;
      const items = await this.http
        .get<BackendCatalogItem[]>(this.buildApiUrl(this.fuelTypesPath), { headers })
        .toPromise();
      if (!items?.length) return;
      this.fuelTypeCatalogItems = items;
      await this.refreshFuelTypeOptions();
    } catch {
      // Keep fallback values if backend fails
    }
  }

  private async loadVehicleTypesFromBackend(): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      if (!headers) return;
      const items = await this.http
        .get<BackendCatalogItem[]>(this.buildApiUrl(this.vehicleTypesPath), { headers })
        .toPromise();
      if (!items?.length) return;
      this.vehicleTypeCatalogItems = items;
      await this.refreshVehicleTypeOptions();
    } catch {
      // Keep fallback values if backend fails
    }
  }

  private async refreshFuelTypeOptions(): Promise<void> {
    if (!this.fuelTypeCatalogItems.length) {
      return;
    }

    this.fuelTypeOptionsSubject.next(this.fuelTypeCatalogItems.map((item) =>
      this.createTranslatedSelectOption(item.key, this.mapFuelTypeValueToI18nKey(item.value))
    ));
  }

  private async refreshVehicleTypeOptions(): Promise<void> {
    if (!this.vehicleTypeCatalogItems.length) {
      return;
    }

    this.vehicleTypeOptionsSubject.next(this.vehicleTypeCatalogItems.map((item) =>
      this.createTranslatedSelectOption(item.key, this.mapVehicleTypeValueToI18nKey(item.value))
    ));
  }

  private createTranslatedSelectOption(value: number, labelKey: string): SelectOption {
    const translate = this.translate;
    return {
      value,
      labelKey,
      get text(): string {
        return translate.instant(labelKey);
      }
    };
  }

  private buildTranslatedOptions(source: Record<number, string>): SelectOption[] {
    return Object.keys(source).map((key) => {
      const value = Number(key);
      return this.createTranslatedSelectOption(value, source[value]);
    });
  }

  private async loadTireRadiusFromBackend(): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      if (!headers) return;
      const radiusValues = await this.http
        .get<number[]>(this.buildApiUrl(this.tireRadiusPath), { headers })
        .toPromise();
      if (!radiusValues?.length) return;
      const sorted = [...new Set(radiusValues)].sort((a, b) => a - b);
      this.tireRadiusOptionsSubject.next(sorted);
    } catch {
      // Optional catalog: keep empty list on failures
    }
  }

  private buildApiUrl(path: string): string {
    const normalizedBaseUrl = this.apiBaseUrl.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (normalizedBaseUrl.endsWith('/api')) {
      return `${normalizedBaseUrl}${normalizedPath}`;
    }
    return `${normalizedBaseUrl}/api${normalizedPath}`;
  }

  private mapFuelTypeValueToI18nKey(value: string): string {
    const map: Record<string, string> = {
      Undefined: 'vehicle.fuel_unknown',
      Fuel95: 'vehicle.fuel_gasoline_95',
      Fuel98: 'vehicle.fuel_gasoline_98',
      Diesel: 'vehicle.fuel_diesel',
      DieselPremium: 'vehicle.fuel_diesel_premium',
      Hybrid: 'vehicle.fuel_hybrid',
      Electric: 'vehicle.fuel_electric',
      NaturalGas: 'vehicle.fuel_natural_gas',
      Fuel95E10: 'vehicle.fuel_95E10',
      Fuel92: 'vehicle.fuel_92',
      Other: 'vehicle.fuel_other'
    };

    return map[value] ?? 'vehicle.fuel_unknown';
  }

  private mapVehicleTypeValueToI18nKey(value: string): string {
    const map: Record<string, string> = {
      Undefined: 'vehicle.type_unknown',
      Sedan: 'vehicle.type_sedan',
      CUV: 'vehicle.type_cuv',
      SUV: 'vehicle.type_suv',
      Hatchback: 'vehicle.type_hatchback',
      Roadster: 'vehicle.type_roadster',
      Pickup: 'vehicle.type_pickup',
      Atv: 'vehicle.type_atv',
      Coupe: 'vehicle.type_coupe',
      SuperCar: 'vehicle.type_supercar',
      Campervan: 'vehicle.type_campervan',
      Minitruck: 'vehicle.type_minitruck',
      Cabriolet: 'vehicle.type_cabriolet',
      Minivan: 'vehicle.type_minivan',
      Truck: 'vehicle.type_truck',
      LightVan: 'vehicle.type_lightvan',
      Van: 'vehicle.type_van',
      BoxTruck: 'vehicle.type_boxtruck',
      Micro: 'vehicle.type_micro',
      Scooter: 'vehicle.type_scooter',
      Motorbike: 'vehicle.type_motorbike',
      Other: 'vehicle.type_other'
    };

    return map[value] ?? 'vehicle.type_unknown';
  }
  getFuelTypeString(id: number): string {
    const fromBackend = this.fuelTypeOptionsSubject.value.find(option => option.value === id);
    return fromBackend?.text ?? this.translate.instant('vehicle.fuel_unknown');
  }

  getFuelTypeNumber(typeLabel: string): number {
    if (!typeLabel) {
      return 0;
    }

    const numericKeyCandidate = Number(typeLabel);
    if (!isNaN(numericKeyCandidate) && this.fuelTypeOptionsSubject.value.some(option => option.value === numericKeyCandidate)) {
      return numericKeyCandidate;
    }

    const searchTerm = typeLabel.trim().toLowerCase();
    for (const option of this.fuelTypeOptionsSubject.value) {
      if (option.text.trim().toLowerCase() === searchTerm) {
        return option.value;
      }
    }

    return 0;
  }


  getAllFuelTypes(): { fuelTypeId: number; fuelTypeDescription: string }[] {
    return this.fuelTypeOptionsSubject.value.map((option) => ({
      fuelTypeId: option.value,
      fuelTypeDescription: option.text
    }));
  }

  getChassisTypeString(id: number): string {
    return this.getVehicleTypeString(id);
  }

  getChassisTypeIcon(id: number): string {
    return this.chassisTypeIconMap[id] ?? 'undefined';
  }

  getChassisTypeNumber(type: string): number {
    if (!type) {
      return 0;
    }
    const numericKeyCandidate = Number(type);
    if (!isNaN(numericKeyCandidate) && this.vehicleTypeOptionsSubject.value.some(option => option.value === numericKeyCandidate)) {
      return numericKeyCandidate;
    }
    const searchTerm = type.trim().toLowerCase();
    const match = this.vehicleTypeOptionsSubject.value.find(option => option.text.trim().toLowerCase() === searchTerm);
    return match?.value ?? 0;
  }

  getAllChassisTypes(): string[] {
    return this.vehicleTypeOptionsSubject.value.map((option) => option.text);
  }

  getServiceTypeString(id: number): string {
    return serviceType[id] ? this.translate.instant(serviceType[id]) : 'Desconocido';
  }

  getAllServiceTypes(): string[] {
    return Object.values(serviceType);
  }

  getOrderStatusString(id: number): string {
    return orderStatus[id] ? this.translate.instant(orderStatus[id]) : 'Desconocido';
  }

  getAllOrderStatus(): string[] {
    return Object.values(orderStatus);
  }

  getModalExtraValue(id: number): string {
    return modalExtras[id] || 'Desconocido';
  }

  getVehicleStatusString(id: number): string {
    return this.translate.instant(`vehicle_status_${id}`);
  }
  getDocumentTypeString(id: number): string {
    return this.translate.instant(`document_type_${id}`);
  }

  getReformTypeString(id: number): string {
    return this.translate.instant(`reform_type_${id}`);
  }

  getChassisTypeForSelect(): { value: number; text: string }[] {
    return [...this.vehicleTypeOptionsSubject.value];
  }

  getFuelTypeForSelect(): { value: number; text: string }[] {
    return [...this.fuelTypeOptionsSubject.value];
  }

  getVehicleTypeForSelect(): { value: number; text: string }[] {
    return [...this.vehicleTypeOptionsSubject.value];
  }

  getVehicleTypeString(id: number): string {
    const match = this.vehicleTypeOptionsSubject.value.find(option => option.value === id);
    return match?.text ?? this.translate.instant('vehicle.type_unknown');
  }

  getTireRadiusForSelect(): number[] {
    return [...this.tireRadiusOptionsSubject.value];
  }

  getGearboxTypeForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(gearboxType);
  }

  getDriveTypeForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(driveType);
  }

  getEcoStickerTypeForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(ecoStickerType);
  }

  getVehicleStatusForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(vehicleStatus);
  }

  getITVStatusForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(itvStatus);
  }

  getCurrenciesForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(currency);
  }

  getPaymentMethodsForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(paymentMethod);
  }

  getInsuranceTypesForSelect(): { value: number; text: string }[] {
    return this.buildTranslatedOptions(insuranceType);
  }
}
